import { useState, useEffect, useCallback } from 'react';
import { buildEndPoint } from '../utils/odataEndPoint';
import api, { cachedApi } from '../services/api';

// Ejemplo de queries:
// [
//   {
//     key: 'status',
//     endpoint: buildOdataEndPoint({...}),
//     transform: data => data.value || [],
//   },
//   {
//     key: 'agencies',
//     endpoint: buildOdataEndPoint({...}),
//     transform: data => data.value.filter(...),
//   }
// ]

// Este hook es para hacer llamadas, exclusivamente de odata, exclusivamente al montarse la componente
// Por defecto las cachea, si no pasar cached = false

export const useODataMultipleQueries = ({
    queries,
    cached = true,
    dependencies,
    condition = true,
}) => {
    const [results, setResults] = useState(() =>
        queries.reduce((acc, { key }) => {
            acc[key] = {
                data: [],
                isLoading: true,
                error: null,
                count: 0,
            };
            return acc;
        }, {})
    );

    const getData = useCallback(async () => {
        // isLoading a true en todas las queries
        setResults(prev =>
            queries.reduce((acc, { key }) => {
                acc[key] = {
                    ...prev[key],
                    isLoading: true,
                };
                return acc;
            }, {})
        );

        const http = cached ? cachedApi : api;

        const responses = await Promise.allSettled(
            queries.map(queryData => {
                const { endpoint } = queryData;
                const { entityName, ...query } = endpoint;
                return http.get(buildEndPoint({ entityName, query }));
            })
        );

        setResults(() =>
            queries.reduce((acc, { key, transform }, idx) => {
                const res = responses[idx];
                if (res.status === 'fulfilled') {
                    const receivedData = res?.value?.data;
                    const receivedValue = receivedData?.value;
                    const receivedCount = parseInt(
                        receivedData?.['odata.count']
                    );
                    acc[key] = {
                        data: transform
                            ? transform(receivedValue)
                            : receivedValue,
                        isLoading: false,
                        error: null,
                        count: receivedCount,
                    };
                } else {
                    acc[key] = {
                        data: [],
                        isLoading: false,
                        error: res.reason,
                        count: 0,
                    };
                }
                return acc;
            }, {})
        );
    }, [queries]);

    useEffect(() => {
        if (condition) {
            getData();
        }
    }, [...dependencies]);

    return results;
};
